import { DialogComponent } from '@theme/dialog';

/**
 * Header account drawers — login (logged out) and account (logged in).
 *
 * Both are thin wrappers over the theme's DialogComponent, exposing open()/close() for the
 * header trigger (on:click="/open") and the close button. They use the shared
 * `dialog-modal dialog-drawer` shell, so the account panel gets the same right-side slide-in
 * and backdrop as the cart / Usage Details drawers.
 *
 * @extends {DialogComponent}
 */
class HeaderDrawerComponent extends DialogComponent {
  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

/**
 * Logged-out drawer. Adds the one thing "Sign in with Shop" does not do for us: tell
 * the page that the shopper is now signed in.
 *
 * The account UI is chosen server-side — `{% if customer %}` in
 * snippets/account-drawer.liquid picks the account drawer or this one — so the markup
 * is fixed at render time. Shop login runs in a popup (the button renders with
 * `ux-mode="windoid"`), and shop-js only navigates the underlying page when it was
 * handed a redirect URI, which the header button is not. So the session changed while
 * the DOM kept saying "logged out", and only a manual refresh fixed it.
 *
 * What shop-js DOES do is announce it, on the <shop-login-button> element itself — never
 * on document or window. Two names matter, and we listen for both because they come from
 * two different code paths with different bubbling:
 *
 *   `completed`                     — chunk.useWindoidMessageHandler dispatches it with
 *                                     bubbles:true on the popup path, but the message
 *                                     handler in chunk.utils_BKnqlkiG dispatches the same
 *                                     name with bubbles:FALSE. A non-bubbling one never
 *                                     reaches us, hence the second listener.
 *   `storefront:signincompleted`    — always bubbles:true, and only fires when
 *                                     `loggedIn && shouldFinalizeLogin`.
 *
 * Either way the session already exists by the time we reload: shop-js awaits the finalize
 * call (`/services/login_with_shop/finalize`, which sets the storefront cookie) BEFORE
 * dispatching. So this is not racing the cookie.
 *
 * Mobile needs nothing from us. `ux-mode="windoid"` falls back to defaultUxMode
 * "redirect" on phones and in-app browsers, so that path navigates the page anyway.
 *
 * Listening on the drawer rather than on document keeps this to OUR button:
 * blocks/follow-on-shop.liquid renders a second <shop-login-button> for the Follow action,
 * and following a shop signs you in too — on document, that would reload the page under a
 * shopper who never asked to log in.
 *
 * Neither event name is documented; both are minified-bundle internals, and Shopify has
 * since removed the equivalent handlers from Dawn and Horizon in favour of the platform's
 * <shopify-account> component. So this must fail silently rather than loudly: if the names
 * change, nothing fires, and we are back to today's stale header — not a broken page.
 */
class LoginDrawerComponent extends HeaderDrawerComponent {
  /** @type {string[]} */
  static SIGN_IN_EVENTS = ['completed', 'storefront:signincompleted'];

  connectedCallback() {
    super.connectedCallback();
    for (const name of LoginDrawerComponent.SIGN_IN_EVENTS) {
      this.addEventListener(name, this.#onShopLoginCompleted);
    }
  }

  disconnectedCallback() {
    for (const name of LoginDrawerComponent.SIGN_IN_EVENTS) {
      this.removeEventListener(name, this.#onShopLoginCompleted);
    }
    super.disconnectedCallback?.();
  }

  /**
   * `completed` is a generic enough name that something else could use it, so confirm the
   * event actually came from the Shop button before throwing the page away. Guard against
   * a double reload too: both names can fire for a single sign-in.
   * @param {Event} event
   */
  #onShopLoginCompleted = (event) => {
    if (this.#reloading) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.tagName.toLowerCase() !== 'shop-login-button') return;

    this.#reloading = true;
    window.location.reload();
  };

  #reloading = false;
}

if (!customElements.get('login-drawer-component')) {
  customElements.define('login-drawer-component', LoginDrawerComponent);
}

if (!customElements.get('account-drawer-component')) {
  customElements.define('account-drawer-component', class extends HeaderDrawerComponent {});
}
